<template>
  <div v-if="hasAnyPermission('transaction-log-view')" class="py-4 container-fluid">
    <ToastPrime position="bottom-left" />
    <div class="card">
      <div class="card-header py-3">
        <div class="d-flex flex-wrap align-items-center gap-3">
          <h5 class="mb-0 fw-bold">Transaction Logs</h5>
          <div class="d-flex flex-wrap gap-2 flex-grow-1 justify-content-center">
            <IconField class="search-field">
              <InputIcon>
                <i class="pi pi-search" />
              </InputIcon>
              <InputText v-model="table.search" type="text" size="small" placeholder="Search Transaction Log"
                class="rounded-pill" style="min-width: 500px;" @keyup="filter" />
            </IconField>
          </div>
          <div class="d-flex flex-wrap gap-2">
            <button type="button" class="btn btn-sm btn-outline-primary m-0" @click="openFilterDialog">
              <i class="pi pi-filter me-1"></i>
              Filter
            </button>
            <button v-if="hasAnyPermission('transaction-log-export')" type="button" class="btn btn-sm btn-primary m-0"
              :disabled="table.exportLoading" @click="exportToExcel">
              <span v-if="table.exportLoading"><i class="fas fa-spinner fa-spin"></i>&nbsp;&nbsp;&nbsp;Wait...</span>
              <span v-else><i class="pi pi-download me-1"></i> Export</span>
            </button>
          </div>
        </div>
      </div>
      <div style="height: 500px; overflow-y: auto;">
        <DataTable :value="table.onLoad ? dummyRows : table.data" removable-sort scrollable scroll-height="flex"
          table-style="min-width: 80rem">
          <Column field="code" header="Code" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data?.code || '' }}
            </template>
          </Column>
          <Column field="type" header="Type" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data?.type || '' }}
            </template>
          </Column>
          <Column field="key" header="Key" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data?.key || '' }}
            </template>
          </Column>
          <Column field="result" header="Result" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              <span v-if="slotProps.data?.result" :class="getResultBadgeClass(slotProps.data.result)">{{
                slotProps.data.result }}</span>
            </template>
          </Column>
          <Column field="duration" header="Duration" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data?.duration || '' }}
            </template>
          </Column>
          <Column field="url" header="URL" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data?.url || '' }}
            </template>
          </Column>
          <Column field="ip" header="IP" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data?.ip || '' }}
            </template>
          </Column>
          <Column field="user_agent" header="User Agent" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data?.user_agent || '' }}
            </template>
          </Column>
          <Column field="message" header="Message" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data?.message || '' }}
            </template>
          </Column>
          <Column field="note" header="Note" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data?.note || '' }}
            </template>
          </Column>
          <Column field="executed_by" header="Executed By" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data?.executed_by || '' }}
            </template>
          </Column>
          <Column field="created_at" header="Created At" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data?.created_at ? formatDate(slotProps.data.created_at) : '' }}
            </template>
          </Column>
          <template #empty>
            <div style="text-align: center; padding: 40px 0;">
              <img src="/no_data.png" alt="No Data" style="max-width: 330px; opacity: 0.7; margin-bottom: 0;" />
              <div style="color: #888; font-size: 1.1em; font-weight: 500;">
                No Data
              </div>
            </div>
          </template>
        </DataTable>
      </div>
      <div class="card-footer pb-0 d-flex justify-content-between align-items-center">
        <BasePagination v-model="table.page.current_pages" :page-count="table.page?.total_pages || 1" size="sm"
          @click="changePage()" />
        <p>
          {{ (table.page.current_pages - 1) * table.page.per_pages + table.data.length }} of {{ table.total }}
        </p>
      </div>
    </div>
    <DialogPrime v-model:visible="filterDialog.visible" modal style="width: 30rem; margin: 10px;">
      <template #header>
        <h5 class="mb-0 fw-bold text-dark">Filter</h5>
      </template>
      <div class="row g-3">
        <div class="col-6">
          <label for="filter-created-start" class="form-label fw-semibold">Created At Start</label>
          <Calendar id="filter-created-start" v-model="filterDialog.form.created_at_start" date-format="yy-mm-dd"
            show-icon placeholder="YYYY-MM-DD" class="w-100" />
        </div>
        <div class="col-6">
          <label for="filter-created-end" class="form-label fw-semibold">Created At End</label>
          <Calendar id="filter-created-end" v-model="filterDialog.form.created_at_end" date-format="yy-mm-dd" show-icon
            placeholder="YYYY-MM-DD" class="w-100" />
        </div>
      </div>
      <template #footer>
        <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
          <ButtonPrime type="button" label="Reset" severity="danger" raised size="small" class="px-4 border-0"
            @click="resetFilter()" />
          <ButtonPrime type="button" severity="contrast" raised size="small" label="Apply Filter" class="px-4"
            @click="applyFilter()" />
        </div>
      </template>
    </DialogPrime>
  </div>
  <NoAccess v-else />
</template>

<script>
import { mapState } from "vuex";
import BasePagination from "@/components/BasePagination.vue";
import moment from "moment";
import api from "@/helpers/api";
import transactionLogService from "@/services/report/appAuditing/transactionLogService";
import NoAccess from "@/components/NoAccess.vue";

export default {
  components: {
    BasePagination,
    NoAccess,
  },
  computed: {
    ...mapState({
      authUserPermissions: (state) => state.auth.userHasPermissions || [],
    }),
  },
  data() {
    return {
      table: {
        onLoad: true,
        search: this.$route.query.search || "",
        total: 0,
        data: [],
        limit: 100,
        page: {
          current_pages: 1,
          per_pages: 5,
          total_pages: 1,
        },
        loadTimeout: null,
        exportLoading: false,
      },
      dummyRows: Array.from({ length: 10 }, () => ({})),
      filterDialog: {
        visible: false,
        form: {
          created_at_start: this.$route.query.created_at_start || null,
          created_at_end: this.$route.query.created_at_end || null,
        }
      },
    };
  },
  mounted() {
    if (this.hasAnyPermission('transaction-log-view')) {
      this.get();
    }
  },
  methods: {
    hasAnyPermission(...perms) {
      return perms.some((p) => this.authUserPermissions.includes(p));
    },
    toYMD(val) {
      if (!val) return "";
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
      const d = val instanceof Date ? val : new Date(val);
      if (isNaN(d.getTime())) return String(val);
      return moment(d).format("YYYY-MM-DD");
    },
    async get() {
      this.table.onLoad = true;
      try {
        const payload = {
          type: ['approval', 'script', 'start', 'notification', 'update_value'],
          code: 'MCR',
          result: ['send_back', 'failed', 'approve', 'success', 'reject']
        };
        const qStart = this.toYMD(this.$route.query.created_at_start);
        const qEnd = this.toYMD(this.$route.query.created_at_end);
        const response = await api(
          this,
          transactionLogService.list(payload, {
            search: this.table.search,
            ...(qStart ? { created_at_start: qStart } : {}),
            ...(qEnd ? { created_at_end: qEnd } : {}),
            limit: this.table.limit,
            page: this.table.page.current_pages
          }),
          false
        );
        const paged = response.data || {};
        this.table.data = paged.data || [];
        this.table.total = paged.total || this.table.data.length;
        this.table.page.total_pages = paged.last_page || 1;
        this.table.page.current_pages = paged.current_page || 1;
        this.table.page.per_pages = paged.per_page || this.table.limit;
      } catch (error) {
        this.table.data = [];
        this.table.total = 0;
        this.table.page.total_pages = 1;
      } finally {
        this.table.onLoad = false;
      }
    },
    filter() {
      clearTimeout(this.table.loadTimeout);
      this.table.loadTimeout = setTimeout(() => {
        const qStart = this.toYMD(this.$route.query.created_at_start);
        const qEnd = this.toYMD(this.$route.query.created_at_end);
        const query = {
          ...(this.table.search ? { search: this.table.search } : {}),
          ...(qStart ? { created_at_start: qStart } : {}),
          ...(qEnd ? { created_at_end: qEnd } : {}),
        };
        this.$router.push({
          path: this.$route.path,
          query: Object.keys(query).length ? query : null,
        });
        this.table.page.current_pages = 1;
        this.get();
      }, 500);
    },
    openFilterDialog() {
      this.filterDialog.visible = true;
    },
    resetFilter() {
      this.filterDialog.form = { created_at_start: null, created_at_end: null };
      this.$router.push({ path: this.$route.path, query: this.table.search ? { search: this.table.search } : null });
      this.table.page.current_pages = 1;
      this.get();
    },
    applyFilter() {
      const qStart = this.toYMD(this.filterDialog.form.created_at_start);
      const qEnd = this.toYMD(this.filterDialog.form.created_at_end);
      const query = {
        ...(this.table.search ? { search: this.table.search } : {}),
        ...(qStart ? { created_at_start: qStart } : {}),
        ...(qEnd ? { created_at_end: qEnd } : {}),
      };
      this.$router.push({ path: this.$route.path, query: Object.keys(query).length ? query : null });
      this.table.page.current_pages = 1;
      this.filterDialog.visible = false;
      this.get();
    },
    changePage() {
      this.get();
    },
    formatDate(date) {
      return date ? moment(date).format("DD MMMM YYYY, HH:mm") : "-";
    },
    getResultBadgeClass(result) {
      const map = {
        'approve': 'badge badge-success',
        'success': 'badge badge-success',
        'failed': 'badge badge-danger',
        'reject': 'badge badge-danger',
        'send_back': 'badge badge-warning',
        'pending': 'badge badge-secondary',
      };
      return map[result] || 'badge badge-secondary';
    },
    exportToExcel() {
      if (this.table.exportLoading) return;
      this.table.exportLoading = true;
      transactionLogService
        .export()
        .then((response) => {
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "transaction_logs.xlsx");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(() => {
          this.$toast?.add?.({ severity: 'error', summary: 'Export failed', detail: 'Gagal mengekspor data', life: 3000 });
        })
        .finally(() => {
          this.table.exportLoading = false;
        });
    },
  },
};
</script>

<style scoped></style>
